import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
    Users, Zap, CheckCircle2, Clock, TrendingUp, LogIn,
    RefreshCw, Search, ChevronRight, AlertCircle, UserPlus, Activity,
} from "lucide-react";

const VIOLET = "#7C3AED";
const CYAN   = "#06B6D4";
const GREEN  = "#10B981";
const ORANGE = "#F59E0B";
const PIE_COLORS = [VIOLET, CYAN, GREEN, ORANGE, "#EC4899", "#EF4444"];

function MetricCard({ label, value, icon: Icon, sub, color = VIOLET }) {
    return (
        <div className="lk-border lk-surface rounded-2xl p-5 flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <span className="lk-label">{label}</span>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: `${color}18`, border: `1px solid ${color}28` }}>
                    <Icon className="w-4 h-4" style={{ color }} />
                </div>
            </div>
            <div className="font-bold text-3xl" style={{ color }}>{value ?? "—"}</div>
            {sub && <div className="text-xs text-[var(--lk-muted)]">{sub}</div>}
        </div>
    );
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="lk-border lk-surface-2 rounded-xl px-3 py-2 text-xs shadow-xl">
            <div className="text-[var(--lk-muted)] mb-1">{label}</div>
            {payload.map(p => (
                <div key={p.name} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></div>
            ))}
        </div>
    );
};

export default function Admin() {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();

    const [analytics, setAnalytics]   = useState(null);
    const [users, setUsers]           = useState([]);
    const [days, setDays]             = useState(14);
    const [search, setSearch]         = useState("");
    const [loading, setLoading]       = useState(true);
    const [impersonating, setImp]     = useState(null);
    const [err, setErr]               = useState("");
    const [tab, setTab]               = useState("overview");

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const [aRes, uRes] = await Promise.all([
                api.get(`/admin/analytics?days=${days}`),
                api.get("/admin/users"),
            ]);
            setAnalytics(aRes.data);
            setUsers(uRes.data);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const handleImpersonate = async (u) => {
        if (!window.confirm(`Login as ${u.email}?`)) return;
        setImp(u.id);
        try {
            const res = await api.post(`/admin/impersonate/${u.id}`);
            localStorage.setItem("lakshmi_token", res.data.access_token);
            setUser(res.data.user);
            navigate("/dashboard");
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setImp(null);
        }
    };

    // Guard: non-admin users see nothing
    if (user?.role !== "admin") {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                    <p className="text-[var(--lk-muted)]">Admin access only.</p>
                </div>
            </div>
        );
    }

    const filteredUsers = users.filter(u =>
        !search || u.email?.toLowerCase().includes(search.toLowerCase()) || u.name?.toLowerCase().includes(search.toLowerCase())
    );

    const a = analytics;

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <div className="lk-label mb-1">Admin Panel</div>
                        <h1 className="font-bold text-3xl tracking-tight">Platform Overview</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <select
                            value={days}
                            onChange={e => setDays(Number(e.target.value))}
                            className="lk-border lk-surface-2 rounded-xl px-3 py-2 text-sm text-[var(--lk-text)] focus:outline-none"
                        >
                            {[7,14,30,60,90].map(d => <option key={d} value={d}>Last {d} days</option>)}
                        </select>
                        <button onClick={load}
                            className="lk-border lk-surface-2 rounded-xl p-2 text-[var(--lk-muted)] hover:text-[var(--lk-text)] transition-colors">
                            <RefreshCw className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {err && (
                    <div className="mb-6 flex items-center gap-2 text-red-400 text-sm lk-border rounded-xl p-3" style={{ borderColor: "rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.05)" }}>
                        <AlertCircle className="w-4 h-4 shrink-0" /> {err}
                    </div>
                )}

                {/* Tabs */}
                <div className="flex gap-1 lk-border lk-surface rounded-xl p-1 w-fit mb-8">
                    {["overview", "users"].map(t => (
                        <button key={t} onClick={() => setTab(t)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${tab === t ? "bg-[var(--lk-primary)] text-white" : "text-[var(--lk-muted)] hover:text-[var(--lk-text)]"}`}>
                            {t}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="flex items-center gap-3 text-[var(--lk-muted)] py-20 justify-center">
                        <RefreshCw className="w-5 h-5 animate-spin" /> Loading…
                    </div>
                ) : tab === "overview" ? (
                    <>
                        {/* Today at a glance */}
                        <div className="lk-border lk-surface rounded-2xl p-4 mb-5">
                            <div className="lk-label mb-3">Today</div>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="text-center">
                                    <div className="font-bold text-2xl" style={{ color: VIOLET }}>{a?.new_users_today ?? "—"}</div>
                                    <div className="text-xs text-[var(--lk-muted)] mt-1 flex items-center justify-center gap-1"><UserPlus className="w-3 h-3" />New signups</div>
                                </div>
                                <div className="text-center border-x border-[var(--lk-border)]">
                                    <div className="font-bold text-2xl" style={{ color: CYAN }}>{a?.gens_today ?? "—"}</div>
                                    <div className="text-xs text-[var(--lk-muted)] mt-1 flex items-center justify-center gap-1"><Zap className="w-3 h-3" />Generations</div>
                                </div>
                                <div className="text-center">
                                    <div className="font-bold text-2xl" style={{ color: GREEN }}>{a?.active_users_today ?? "—"}</div>
                                    <div className="text-xs text-[var(--lk-muted)] mt-1 flex items-center justify-center gap-1"><Activity className="w-3 h-3" />Active users</div>
                                </div>
                            </div>
                        </div>

                        {/* Metric cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                            <MetricCard label="Total Users"       value={a?.total_users}       icon={Users}       color={VIOLET} sub="All registered accounts" />
                            <MetricCard label="Total Generations" value={a?.total_generations} icon={Zap}         color={CYAN}   sub="All time" />
                            <MetricCard label="Success Rate"      value={a?.success_rate ? `${Math.round(a.success_rate * 100)}%` : "—"} icon={CheckCircle2} color={GREEN} />
                            <MetricCard label="Avg Latency"       value={a?.avg_latency_ms ? `${(a.avg_latency_ms/1000).toFixed(1)}s` : "—"} icon={Clock} color={ORANGE} sub="Per generation" />
                        </div>

                        {/* Charts row */}
                        <div className="grid lg:grid-cols-3 gap-5 mb-8">
                            {/* Daily activity */}
                            <div className="lg:col-span-2 lk-border lk-surface rounded-2xl p-5">
                                <div className="font-semibold mb-4">Daily Generations</div>
                                <ResponsiveContainer width="100%" height={220}>
                                    <AreaChart data={a?.daily || []} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                                        <defs>
                                            <linearGradient id="gViolet" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%"  stopColor={VIOLET} stopOpacity={0.3} />
                                                <stop offset="95%" stopColor={VIOLET} stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#1E2140" />
                                        <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#8B8FAD" }} tickLine={false} />
                                        <YAxis tick={{ fontSize: 10, fill: "#8B8FAD" }} tickLine={false} axisLine={false} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Area type="monotone" dataKey="generations" name="Generations" stroke={VIOLET} fill="url(#gViolet)" strokeWidth={2} dot={false} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Agent breakdown pie */}
                            <div className="lk-border lk-surface rounded-2xl p-5">
                                <div className="font-semibold mb-4">Agent Usage</div>
                                {a?.by_agent?.length ? (
                                    <ResponsiveContainer width="100%" height={220}>
                                        <PieChart>
                                            <Pie data={a.by_agent} dataKey="count" nameKey="agent_name" cx="50%" cy="50%" outerRadius={75} strokeWidth={0}>
                                                {a.by_agent.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                            </Pie>
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend iconSize={8} iconType="circle" wrapperStyle={{ fontSize: "10px", color: "#8B8FAD" }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : <p className="text-sm text-[var(--lk-muted)] mt-8 text-center">No data yet</p>}
                            </div>
                        </div>

                        {/* Top users bar chart */}
                        {a?.top_users?.length > 0 && (
                            <div className="lk-border lk-surface rounded-2xl p-5 mb-8">
                                <div className="font-semibold mb-4">Top Users by Generations</div>
                                <ResponsiveContainer width="100%" height={180}>
                                    <BarChart data={a.top_users.slice(0, 8)} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#1E2140" />
                                        <XAxis dataKey="email" tick={{ fontSize: 9, fill: "#8B8FAD" }} tickLine={false}
                                            tickFormatter={v => v?.split("@")[0]} />
                                        <YAxis tick={{ fontSize: 10, fill: "#8B8FAD" }} tickLine={false} axisLine={false} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="generations" name="Generations" fill={CYAN} radius={[4,4,0,0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </>
                ) : (
                    /* Users tab */
                    <>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="relative flex-1 max-w-sm">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--lk-muted)]" />
                                <input
                                    type="text"
                                    placeholder="Search by name or email…"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="w-full lk-border lk-surface-2 rounded-xl pl-9 pr-4 py-2.5 text-sm text-[var(--lk-text)] placeholder-[var(--lk-muted)] focus:outline-none focus:border-[var(--lk-primary)]"
                                />
                            </div>
                            <span className="text-xs text-[var(--lk-muted)]">{filteredUsers.length} users</span>
                        </div>

                        <div className="lk-border lk-surface rounded-2xl overflow-hidden">
                            {/* Table header */}
                            <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-[var(--lk-border)] text-xs text-[var(--lk-muted)] font-medium uppercase tracking-wide">
                                <div className="col-span-4">User</div>
                                <div className="col-span-2 text-center">Role</div>
                                <div className="col-span-2 text-center">Generations</div>
                                <div className="col-span-2 text-center">Joined</div>
                                <div className="col-span-2 text-right">Action</div>
                            </div>

                            {filteredUsers.length === 0 ? (
                                <div className="px-5 py-12 text-center text-sm text-[var(--lk-muted)]">No users found</div>
                            ) : filteredUsers.map((u, i) => (
                                <div key={u.id}
                                    className={`grid grid-cols-12 gap-2 px-5 py-4 items-center hover:bg-[var(--lk-surface-2)] transition-colors ${i > 0 ? "border-t border-[var(--lk-border)]" : ""}`}>
                                    {/* Avatar + name */}
                                    <div className="col-span-4 flex items-center gap-3 min-w-0">
                                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                                            style={{ background: `hsl(${(u.email?.charCodeAt(0) || 0) * 17 % 360}, 60%, 45%)` }}>
                                            {(u.name || u.email || "?")[0].toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{u.name || "—"}</div>
                                            <div className="text-xs text-[var(--lk-muted)] truncate">{u.email}</div>
                                        </div>
                                    </div>

                                    {/* Role badge */}
                                    <div className="col-span-2 flex justify-center">
                                        <span className={`text-[0.65rem] font-semibold px-2 py-0.5 rounded-full ${
                                            u.role === "admin"
                                                ? "bg-[rgba(124,58,237,0.15)] text-[var(--lk-primary)] border border-[rgba(124,58,237,0.3)]"
                                                : "bg-[var(--lk-surface-2)] text-[var(--lk-muted)] border border-[var(--lk-border)]"
                                        }`}>
                                            {u.role || "user"}
                                        </span>
                                    </div>

                                    {/* Generations */}
                                    <div className="col-span-2 text-center font-mono text-sm text-[var(--lk-primary)]">
                                        {u.total_generations ?? "—"}
                                    </div>

                                    {/* Joined */}
                                    <div className="col-span-2 text-center text-xs text-[var(--lk-muted)]">
                                        {u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" }) : "—"}
                                    </div>

                                    {/* Impersonate */}
                                    <div className="col-span-2 flex justify-end">
                                        {u.role !== "admin" && (
                                            <button
                                                onClick={() => handleImpersonate(u)}
                                                disabled={impersonating === u.id}
                                                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[var(--lk-border)] text-[var(--lk-muted)] hover:text-[var(--lk-primary)] hover:border-[var(--lk-primary)] transition-colors disabled:opacity-40"
                                                title={`Login as ${u.email}`}
                                            >
                                                {impersonating === u.id
                                                    ? <RefreshCw className="w-3 h-3 animate-spin" />
                                                    : <LogIn className="w-3 h-3" />}
                                                Login as
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
